/**
 * Camera location overlay loader for Pathfinder + COP maps.
 *
 * Loads one or more GeoJSON FeatureCollections from configured URLs
 * (default: /overlays/camera-locations.geojson). Polls periodically so
 * updated files appear without a page refresh.
 */

export type CameraLocationProperties = {
  camera_id: string;
  camera_name: string;
  status?: string | null;
  /** Category slug (alpr, speed, traffic, police, municipal, public, private, gunshot, other). */
  category?: string | null;
  /** Where the record came from (OpenStreetMap, agency export, ...). */
  source?: string | null;
  /** Derived road / intersection label ("Main St & 5th Ave"), admin-triggered. */
  location?: string | null;
  latitude: number;
  longitude: number;
  /** Original property bag for "additional attributes" in the popup. */
  attributes: Record<string, unknown>;
  marker_type: 'camera';
};

/** Mirrors backend CATEGORY_LABELS (app/services/camera_overlay_ingest.py). */
export const CAMERA_CATEGORY_LABELS: Record<string, string> = {
  alpr: 'ALPR',
  speed: 'Speed / red-light',
  traffic: 'Traffic',
  police: 'Police',
  municipal: 'Municipal',
  public: 'Public surveillance',
  private: 'Private',
  gunshot: 'Gunshot detector',
  other: 'Other',
};

/** Marker colors per category; unknown slugs fall back to CAMERA_OVERLAY_COLOR. */
export const CAMERA_CATEGORY_COLORS: Record<string, string> = {
  alpr: '#d32f2f',
  speed: '#f57c00',
  traffic: '#fbc02d',
  police: '#1565c0',
  municipal: '#6a1b9a',
  public: '#00838f',
  private: '#546e7a',
  gunshot: '#ad1457',
  other: '#455a64',
};

/**
 * What to call a camera in labels / titles: the derived road or intersection
 * when an admin has run "Derive road names", else the supplied name (when it
 * is a real name rather than the id), else the id.
 */
export function cameraDisplayLabel(
  camera: Pick<CameraLocationProperties, 'camera_id' | 'camera_name' | 'location'>
): string {
  const loc = (camera.location || '').trim();
  if (loc) return loc;
  const name = (camera.camera_name || '').trim();
  if (name && name !== camera.camera_id) return name;
  return camera.camera_id;
}

/** Mapbox expression equivalent of `cameraDisplayLabel` for symbol layers. */
export const CAMERA_LABEL_EXPRESSION: unknown[] = [
  'coalesce',
  ['get', 'location'],
  ['get', 'camera_name'],
  ['get', 'camera_id'],
];

export function cameraCategoryLabel(slug?: string | null): string {
  if (!slug) return CAMERA_CATEGORY_LABELS.other;
  return (
    CAMERA_CATEGORY_LABELS[slug] ||
    slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export type CameraFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Point,
  CameraLocationProperties
>;

const EMPTY_COLLECTION: CameraFeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

/** Layer ids shared by Pathfinder and COP (interactiveLayerIds + Source/Layer). */
export const CAMERA_OVERLAY_LAYER_IDS = {
  source: 'camera-locations',
  clusters: 'camera-locations-clusters',
  clusterCount: 'camera-locations-cluster-count',
  unclustered: 'camera-locations-unclustered',
  labels: 'camera-locations-labels',
} as const;

export const CAMERA_OVERLAY_COLOR = '#00838f';

const DEFAULT_URLS = ['/overlays/camera-locations.geojson'];
const REFRESH_MS = 60_000;

function configuredUrls(): string[] {
  const raw = (process.env.REACT_APP_CAMERA_OVERLAY_URLS || '').trim();
  if (!raw) return DEFAULT_URLS;
  return raw
    .split(',')
    .map((u) => u.trim())
    .filter(Boolean);
}

function pickString(
  props: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = props[key];
    if (value == null || value === '') continue;
    return String(value);
  }
  return undefined;
}

function normalizeFeature(
  feature: GeoJSON.Feature,
  index: number
): GeoJSON.Feature<GeoJSON.Point, CameraLocationProperties> | null {
  if (!feature.geometry || feature.geometry.type !== 'Point') return null;
  const coords = feature.geometry.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const longitude = Number(coords[0]);
  const latitude = Number(coords[1]);
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;

  const raw = (feature.properties || {}) as Record<string, unknown>;
  const camera_id =
    pickString(raw, ['camera_id', 'cameraId', 'id', 'source_row', 'uid']) ||
    `camera-${index + 1}`;
  // Derived road / intersection label written by "Derive road names" (admin).
  // Keep it as its own field: cameraDisplayLabel / CAMERA_LABEL_EXPRESSION
  // prefer it over the supplied name everywhere a camera is captioned.
  const location = pickString(raw, ['location']) || null;
  const camera_name =
    pickString(raw, [
      'camera_name',
      'cameraName',
      'name',
      'title',
      'query',
      'label',
    ]) || camera_id;
  const status = pickString(raw, ['status', 'state', 'camera_status']) || null;
  const category = pickString(raw, ['category']) || null;
  const source = pickString(raw, ['source']) || null;

  const reserved = new Set([
    'camera_id',
    'cameraId',
    'camera_name',
    'cameraName',
    'name',
    'title',
    'status',
    'state',
    'camera_status',
    'category',
    'source',
    'location',
    'overlay_file_id',
    'overlay_file_name',
    'marker_type',
    'latitude',
    'longitude',
    'cluster',
    'point_count',
    'point_count_abbreviated',
  ]);
  const attributes: Record<string, unknown> = {};
  // The backend ships the original property bag under `attributes`
  // (object or JSON string); merge it instead of nesting it again.
  const nested = raw.attributes;
  if (typeof nested === 'string') {
    try {
      Object.assign(attributes, JSON.parse(nested) as Record<string, unknown>);
    } catch {
      /* ignore malformed attribute bag */
    }
  } else if (nested && typeof nested === 'object') {
    Object.assign(attributes, nested as Record<string, unknown>);
  }
  Object.entries(raw).forEach(([key, value]) => {
    if (reserved.has(key) || key === 'attributes') return;
    if (value == null || value === '') return;
    attributes[key] = value;
  });

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [longitude, latitude],
    },
    properties: {
      camera_id,
      camera_name,
      status,
      category,
      source,
      location,
      latitude,
      longitude,
      // Mapbox property bags should be flat / JSON-serializable scalars.
      attributes: JSON.stringify(attributes) as unknown as Record<string, unknown>,
      marker_type: 'camera',
    },
  };
}

function mergeCollections(
  collections: GeoJSON.FeatureCollection[]
): CameraFeatureCollection {
  const features: CameraFeatureCollection['features'] = [];
  collections.forEach((collection) => {
    (collection.features || []).forEach((feature, index) => {
      const normalized = normalizeFeature(feature, features.length + index);
      if (normalized) features.push(normalized);
    });
  });
  return { type: 'FeatureCollection', features };
}

/** Normalize any FeatureCollection (URL or uploaded file) into camera markers. */
export function normalizeCameraCollection(
  collection: GeoJSON.FeatureCollection
): CameraFeatureCollection {
  return mergeCollections([collection]);
}

/**
 * Parse a .geojson / .json text payload into a normalized camera FeatureCollection.
 * Accepts a FeatureCollection or a bare Feature with Point geometry.
 */
export function parseCameraOverlayText(text: string, fileName?: string): CameraFeatureCollection {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`${fileName || 'File'} is not valid JSON`);
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error(`${fileName || 'File'} is not a GeoJSON object`);
  }

  const obj = parsed as { type?: string; features?: unknown; geometry?: { type?: string } };
  if (obj.type === 'FeatureCollection' && Array.isArray(obj.features)) {
    const normalized = normalizeCameraCollection(obj as GeoJSON.FeatureCollection);
    if (normalized.features.length === 0) {
      throw new Error(`${fileName || 'File'} has no Point features`);
    }
    return normalized;
  }
  if (obj.type === 'Feature' && obj.geometry?.type === 'Point') {
    const normalized = normalizeCameraCollection({
      type: 'FeatureCollection',
      features: [obj as GeoJSON.Feature],
    });
    if (normalized.features.length === 0) {
      throw new Error(`${fileName || 'File'} is not a valid Point feature`);
    }
    return normalized;
  }
  throw new Error(`${fileName || 'File'} must be a GeoJSON FeatureCollection of Points`);
}

export async function parseCameraOverlayFile(file: File): Promise<CameraFeatureCollection> {
  const text = await file.text();
  return parseCameraOverlayText(text, file.name);
}

/**
 * Mapbox cluster click + GeoJSON properties stringify nested objects.
 * Rehydrate attributes if they arrived as a JSON string.
 */
export function parseCameraFeatureProperties(
  properties: Record<string, unknown> | null | undefined
): CameraLocationProperties | null {
  if (!properties) return null;
  const markerType = properties.marker_type;
  if (markerType != null && markerType !== 'camera') return null;
  // Clusters omit marker_type; unclustered clicks always have camera fields.
  if (properties.camera_id == null && properties.camera_name == null && markerType !== 'camera') {
    return null;
  }

  let attributes: Record<string, unknown> = {};
  const rawAttrs = properties.attributes;
  if (typeof rawAttrs === 'string') {
    try {
      attributes = JSON.parse(rawAttrs) as Record<string, unknown>;
    } catch {
      attributes = {};
    }
  } else if (rawAttrs && typeof rawAttrs === 'object') {
    attributes = rawAttrs as Record<string, unknown>;
  }

  return {
    camera_id: String(properties.camera_id || ''),
    camera_name: String(properties.camera_name || properties.camera_id || 'Camera'),
    status: properties.status != null && properties.status !== '' ? String(properties.status) : null,
    category:
      properties.category != null && properties.category !== '' ? String(properties.category) : null,
    source: properties.source != null && properties.source !== '' ? String(properties.source) : null,
    location:
      properties.location != null && properties.location !== '' ? String(properties.location) : null,
    latitude: Number(properties.latitude),
    longitude: Number(properties.longitude),
    attributes,
    marker_type: 'camera',
  };
}

type CacheEntry = { etag: string | null; body: string; collection: GeoJSON.FeatureCollection };

const urlCache = new Map<string, CacheEntry>();

async function fetchOne(url: string): Promise<GeoJSON.FeatureCollection | null> {
  const headers: HeadersInit = { Accept: 'application/geo+json, application/json' };
  const cached = urlCache.get(url);
  if (cached?.etag) {
    headers['If-None-Match'] = cached.etag;
  }

  try {
    const response = await fetch(url, { headers, cache: 'no-cache' });
    if (response.status === 304 && cached) {
      return cached.collection;
    }
    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`[camera-overlay] Not found: ${url}`);
        return null;
      }
      throw new Error(`HTTP ${response.status}`);
    }
    const body = await response.text();
    if (cached && cached.body === body) {
      return cached.collection;
    }
    const parsed = JSON.parse(body) as GeoJSON.FeatureCollection;
    if (!parsed || parsed.type !== 'FeatureCollection' || !Array.isArray(parsed.features)) {
      console.warn(`[camera-overlay] Invalid FeatureCollection at ${url}`);
      return null;
    }
    urlCache.set(url, {
      etag: response.headers.get('ETag'),
      body,
      collection: parsed,
    });
    return parsed;
  } catch (err) {
    console.warn(`[camera-overlay] Failed to load ${url}:`, err);
    return cached?.collection ?? null;
  }
}

export async function loadCameraLocationOverlays(
  urls: string[] = configuredUrls()
): Promise<CameraFeatureCollection> {
  if (urls.length === 0) return EMPTY_COLLECTION;
  const results = await Promise.all(urls.map((url) => fetchOne(url)));
  const collections = results.filter((c): c is GeoJSON.FeatureCollection => !!c);
  if (collections.length === 0) return EMPTY_COLLECTION;
  return mergeCollections(collections);
}

export function getCameraOverlayRefreshMs(): number {
  const raw = Number(process.env.REACT_APP_CAMERA_OVERLAY_REFRESH_MS);
  if (Number.isFinite(raw) && raw >= 5_000) return raw;
  return REFRESH_MS;
}

export function getCameraOverlayUrls(): string[] {
  return configuredUrls();
}

export { EMPTY_COLLECTION as EMPTY_CAMERA_COLLECTION };
